/**
 * Device fingerprinting utility for account sharing prevention
 */

export class DeviceFingerprint {
  /**
   * Generate device fingerprint data
   */
  static generateFingerprint() {
    const fingerprint = {
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezoneOffset: new Date().getTimezoneOffset(),
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
    };

    // Add additional browser features if available
    if (navigator.hardwareConcurrency) {
      fingerprint.hardwareConcurrency = navigator.hardwareConcurrency;
    }

    if (navigator.deviceMemory) {
      fingerprint.deviceMemory = navigator.deviceMemory;
    }

    // Add canvas fingerprint (lightweight version)
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint test', 2, 2);
      fingerprint.canvasFingerprint = canvas.toDataURL().slice(-50); // Last 50 chars
    } catch (e) {
      fingerprint.canvasFingerprint = 'unavailable';
    }

    return fingerprint;
  }

  /**
   * Get device type based on user agent and screen size
   */
  static getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.screen.width;

    if (/mobile|android|iphone/.test(userAgent)) {
      return 'mobile';
    } else if (/tablet|ipad/.test(userAgent) || (screenWidth >= 768 && screenWidth <= 1024)) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  /**
   * Get human-readable device name
   */
  static getDeviceName() {
    const userAgent = navigator.userAgent;
    
    // Browser detection
    let browser = 'Unknown Browser';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browser = 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
    } else if (userAgent.includes('Edg')) {
      browser = 'Edge';
    }

    // OS detection
    let os = 'Unknown OS';
    if (userAgent.includes('Windows')) {
      os = 'Windows';
    } else if (userAgent.includes('Mac')) {
      os = 'macOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      os = 'iOS';
    }

    return `${browser} on ${os}`;
  }

  /**
   * Set device fingerprint headers for API requests
   */
  static getDeviceHeaders() {
    const fingerprint = this.generateFingerprint();
    
    return {
      'X-Screen-Resolution': fingerprint.screenResolution,
      'X-Timezone-Offset': fingerprint.timezoneOffset.toString(),
      'X-Device-Type': this.getDeviceType(),
      'X-Device-Name': this.getDeviceName(),
    };
  }

  /**
   * Check if device fingerprinting is supported
   */
  static isSupported() {
    return typeof navigator !== 'undefined' && 
           typeof window.screen !== 'undefined' && 
           typeof document !== 'undefined';
  }
}

/**
 * HTTP interceptor to add device headers to all API requests
 */
export const addDeviceHeaders = (config) => {
  if (DeviceFingerprint.isSupported()) {
    const deviceHeaders = DeviceFingerprint.getDeviceHeaders();
    config.headers = {
      ...config.headers,
      ...deviceHeaders,
    };
  }
  return config;
};

/**
 * Device limit error handler
 */
export const handleDeviceLimitError = (error) => {
  if (error.response && error.response.status === 403) {
    const data = error.response.data;
    if (data.error === 'device_limit_exceeded') {
      return {
        isDeviceLimitError: true,
        message: data.message,
        maxDevices: data.max_devices,
        activeDevices: data.active_devices,
      };
    }
  }
  return { isDeviceLimitError: false };
};

export default DeviceFingerprint;
